import { useEffect, useLayoutEffect, useState } from 'react';

// Shared clock and easing for the onboarding product demos. Each demo is a pure function of one
// time value, so it loops cleanly, pauses off screen, and can be recorded frame by frame.

export const clamp01 = (value) => Math.min(1, Math.max(0, value));
export const span = (t, start, end) => clamp01((t - start) / (end - start));
export const easeOut = (x) => 1 - (1 - x) ** 3;
export const easeInOut = (x) => (x < 0.5 ? 4 * x ** 3 : 1 - ((-2 * x + 2) ** 3) / 2);
export const mix = (a, b, x) => a + (b - a) * x;

export function useReducedMotionPreference() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

export function useInView(ref) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

// Returns the demo time in ms. freezeAt pins it (recording); reduced motion shows the still frame.
export function useDemoTime({ frameRef, loopMs, stillMs, freezeAt = null }) {
  const reduced = useReducedMotionPreference();
  const visible = useInView(frameRef);
  const running = visible && freezeAt === null && !reduced;
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!running) return undefined;
    let frame;
    let origin;
    const tick = (now) => {
      if (origin === undefined) origin = now - t;
      setT((now - origin) % loopMs);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // The clock resumes from where it paused; t is read only on restart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, loopMs]);
  if (freezeAt !== null) return freezeAt;
  return reduced ? stillMs : t;
}

// Scales a fixed design stage to the width of its frame.
export function useStageScale(frameRef, stageWidth) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const measure = () => setScale(frame.clientWidth / stageWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frameRef, stageWidth]);
  return scale;
}
